/**
 * SITE CONTENT — the CMS screen.
 *
 * One card per content block (lib/content.ts BLOCK_SPECS): plain fields,
 * image fields with a picker into the media library, and repeatable lists
 * (dates, locations, questions …) with add / remove / reorder. Save writes
 * the whole block (PUT /api/admin/content); Reset deletes the row so the code
 * default takes over again. Everything is LIVE the moment it is saved — the
 * public pages re-read /api/content within a minute.
 */
import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, ApiError, AssetCreated, type Asset } from "@alight/contracts";
import {
  Badge,
  Banner,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@alight/ui";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  Image as ImageIcon,
  Plus,
  RotateCcw,
  Trash2,
  Upload,
} from "lucide-react";
import { usePortalSeo } from "@alight/enrolment-ui";
import {
  BLOCK_SPECS,
  DEFAULTS,
  resolveBlock,
  type BlockKey,
  type BlockSpec,
  type FieldSpec,
  type ListSpec,
} from "../../lib/content";
import { weekdayLabel } from "../../lib/season";

export const Route = createFileRoute("/admin/content")({
  component: AdminContent,
});

/** Vite proxies `/api` to the Worker in dev; relative base is correct. */
const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? "";
const R2_PUBLIC_BASE =
  (import.meta.env.VITE_R2_PUBLIC_BASE_URL as string | undefined) ??
  "https://connect-assets.alightcreative.com.au";

/** Dev-only identity for the Worker's x-admin-email bypass (see lib/api.ts). */
const DEV_HEADERS: Record<string, string> = import.meta.env.DEV
  ? { "x-admin-email": (import.meta.env.VITE_ADMIN_EMAIL as string | undefined) ?? "vc.le21@gmail.com" }
  : {};

interface SavedBlock {
  key: string;
  value: unknown;
  updated_at: string;
  updated_by: string | null;
}

async function fetchSaved(): Promise<SavedBlock[]> {
  const res = await fetch(`${API_BASE}/api/admin/content`, {
    headers: { Accept: "application/json", ...DEV_HEADERS },
    credentials: "same-origin",
  });
  if (!res.ok) throw new ApiError(res.status, `GET /api/admin/content failed (${res.status})`);
  const data = (await res.json()) as { blocks: SavedBlock[] };
  return data.blocks;
}

async function putBlock(key: string, value: unknown): Promise<void> {
  const res = await fetch(`${API_BASE}/api/admin/content`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", Accept: "application/json", ...DEV_HEADERS },
    credentials: "same-origin",
    body: JSON.stringify({ key, value }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new ApiError(res.status, text || `PUT /api/admin/content failed (${res.status})`);
  }
}

/* ────────────────────────────── screen ─────────────────────────────── */

function AdminContent() {
  usePortalSeo("Site content");
  const saved = useQuery({ queryKey: ["admin", "content"], queryFn: fetchSaved });
  const byKey = React.useMemo(
    () => new Map((saved.data ?? []).map((b) => [b.key, b])),
    [saved.data],
  );

  return (
    <section className="space-y-8">
      <header>
        <h1 className="font-heading text-2xl font-bold text-ink">Site content</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted">
          Everything on the public pages — words, dates, locations, photos. Changes are live as soon
          as you save. A block marked <Badge variant="accent">Customised</Badge> has been edited here;
          Reset puts the original content back.
        </p>
      </header>

      {saved.isLoading && <p className="text-sm text-muted">Loading content…</p>}
      {saved.isError && (
        <Banner variant="error" title="Couldn't load the saved content">
          {saved.error instanceof ApiError ? saved.error.message : "Please refresh and try again."}
        </Banner>
      )}

      {saved.isSuccess &&
        BLOCK_SPECS.map((spec) => (
          <BlockEditor key={spec.key} spec={spec} saved={byKey.get(spec.key) ?? null} />
        ))}
    </section>
  );
}

/* ─────────────────────────── one block ─────────────────────────────── */

type Doc = Record<string, unknown>;

function BlockEditor({ spec, saved }: { spec: BlockSpec; saved: SavedBlock | null }) {
  const qc = useQueryClient();
  // The form edits a resolved copy (saved over default) so every field has a
  // value; the `saved` identity is the reset point.
  const initial = React.useMemo(
    () => resolveBlock(spec.key, saved?.value) as unknown as Doc,
    [spec.key, saved],
  );
  const [doc, setDoc] = React.useState<Doc>(initial);
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => setDoc(initial), [initial]);
  const dirty = JSON.stringify(doc) !== JSON.stringify(initial);

  const save = useMutation({
    mutationFn: (value: unknown) => putBlock(spec.key, value),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "content"] });
      void qc.invalidateQueries({ queryKey: ["site-content"] });
    },
  });

  const setField = (key: string, value: unknown) => setDoc((d) => ({ ...d, [key]: value }));

  return (
    <Card>
      <CardContent className="p-0">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex w-full items-start justify-between gap-4 p-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <span>
            <span className="flex flex-wrap items-center gap-2">
              <span className="font-heading text-lg font-bold text-ink">{spec.title}</span>
              {saved && <Badge variant="accent">Customised</Badge>}
              {dirty && <Badge variant="outline">Unsaved changes</Badge>}
            </span>
            <span className="mt-1 block text-sm text-muted">{spec.description}</span>
            {saved && (
              <span className="mt-1 block text-xs text-muted">
                Last saved {new Date(saved.updated_at).toLocaleString()}
                {saved.updated_by ? ` by ${saved.updated_by}` : ""}
              </span>
            )}
          </span>
          <span className="shrink-0 text-sm font-semibold text-primary">{open ? "Close" : "Edit"}</span>
        </button>

        {open && (
          <div className="space-y-6 border-t border-border p-5">
            <div className="grid gap-5 md:grid-cols-2">
              {spec.fields.map((f) => (
                <Field
                  key={f.key}
                  spec={f}
                  id={`${spec.key}-${f.key}`}
                  value={String(doc[f.key] ?? "")}
                  onChange={(v) => setField(f.key, v)}
                />
              ))}
            </div>

            {spec.lists.map((list) => (
              <ListEditor
                key={list.key}
                spec={list}
                idPrefix={`${spec.key}-${list.key}`}
                items={Array.isArray(doc[list.key]) ? (doc[list.key] as Doc[]) : []}
                onChange={(items) => setField(list.key, items)}
              />
            ))}

            {save.isError && (
              <Banner variant="error">
                {save.error instanceof ApiError ? save.error.message : "Saving failed — please try again."}
              </Banner>
            )}
            {save.isSuccess && !dirty && (
              <Banner variant="success">Saved — the site shows this within a minute.</Banner>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <Button type="button" onClick={() => save.mutate(doc)} disabled={!dirty || save.isPending}>
                <Check className="mr-1.5 h-4 w-4" aria-hidden />
                {save.isPending ? "Saving…" : "Save"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setDoc(initial)} disabled={!dirty}>
                Discard changes
              </Button>
              {saved && (
                <Button
                  type="button"
                  variant="ghost"
                  className="ml-auto text-muted"
                  onClick={() => {
                    if (window.confirm(`Reset "${spec.title}" to the original content? This can't be undone.`)) {
                      save.mutate(null);
                    }
                  }}
                  disabled={save.isPending}
                >
                  <RotateCcw className="mr-1.5 h-4 w-4" aria-hidden />
                  Reset to original
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────────── fields ────────────────────────────── */

function Field({
  spec,
  id,
  value,
  onChange,
  hint,
}: {
  spec: FieldSpec;
  id: string;
  value: string;
  onChange: (v: string) => void;
  /** Extra line shown under the field — used for the weekday next to a date. */
  hint?: string;
}) {
  const wide = spec.kind === "multiline" || spec.kind === "image";
  return (
    <div className={wide ? "space-y-1.5 md:col-span-2" : "space-y-1.5"}>
      <Label htmlFor={id}>{spec.label}</Label>
      {spec.kind === "multiline" ? (
        <Textarea id={id} rows={value.length > 240 ? 6 : 3} value={value} onChange={(e) => onChange(e.target.value)} />
      ) : spec.kind === "image" ? (
        <ImageField id={id} value={value} onChange={onChange} />
      ) : spec.kind === "date" ? (
        <Input id={id} type="date" value={value} onChange={(e) => onChange(e.target.value)} />
      ) : spec.kind === "select" ? (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger id={id}>
            <SelectValue placeholder="Choose…" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {(spec.options ?? []).map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      ) : (
        <Input id={id} type={spec.kind === "url" ? "text" : "text"} value={value} onChange={(e) => onChange(e.target.value)} />
      )}
      {hint && <p className="text-xs font-medium text-primary">{hint}</p>}
      {spec.help && <p className="text-xs text-muted">{spec.help}</p>}
    </div>
  );
}

function ImageField({ id, value, onChange }: { id: string; value: string; onChange: (v: string) => void }) {
  const [pickerOpen, setPickerOpen] = React.useState(false);
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
      {value ? (
        <img
          src={value}
          alt=""
          className="h-24 w-36 shrink-0 rounded-card border border-border bg-card object-cover"
        />
      ) : (
        <div className="flex h-24 w-36 shrink-0 items-center justify-center rounded-card border border-dashed border-border text-muted">
          <ImageIcon className="h-6 w-6" aria-hidden />
        </div>
      )}
      <div className="min-w-0 flex-1 space-y-2">
        <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} placeholder="/images/… or https://…" />
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" variant="outline" onClick={() => setPickerOpen(true)}>
            <ImageIcon className="mr-1.5 h-4 w-4" aria-hidden />
            Choose or upload
          </Button>
        </div>
      </div>
      <ImagePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(url) => {
          onChange(url);
          setPickerOpen(false);
        }}
      />
    </div>
  );
}

/** The media library (host portal → Assets) as a picker, with an upload. */
function ImagePicker({ open, onClose, onPick }: { open: boolean; onClose: () => void; onPick: (url: string) => void }) {
  const api = React.useMemo(() => apiClient(API_BASE), []);
  const qc = useQueryClient();
  const assets = useQuery({
    queryKey: ["admin", "assets"],
    queryFn: () => api.listAssets(),
    enabled: open,
  });
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const params = new URLSearchParams({ filename: file.name, category: "site" });
      const res = await fetch(`${API_BASE}/api/admin/assets?${params.toString()}`, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream", ...DEV_HEADERS },
        body: file,
        credentials: "same-origin",
      });
      const text = await res.text();
      if (!res.ok) throw new ApiError(res.status, text || `Upload failed (${res.status})`);
      return AssetCreated.parse(JSON.parse(text));
    },
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: ["admin", "assets"] });
      onPick(created.url);
    },
    onError: (err) => setUploadError(err instanceof ApiError ? err.message : "Upload failed."),
  });

  const images = (assets.data ?? []).filter((a: Asset) => a.kind === "image");

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Choose an image</DialogTitle>
          <DialogDescription>
            Pick from the media library, or upload a new photo (JPEG or PNG, ideally under 1 MB).
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded border border-border bg-card px-3 py-2 text-sm font-semibold text-ink hover:bg-band">
            <Upload className="h-4 w-4" aria-hidden />
            {upload.isPending ? "Uploading…" : "Upload a photo"}
            <input
              type="file"
              accept="image/*"
              className="sr-only"
              disabled={upload.isPending}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setUploadError(null);
                  upload.mutate(file);
                }
                e.target.value = "";
              }}
            />
          </label>
          {uploadError && <span className="text-sm text-error">{uploadError}</span>}
        </div>

        <div className="max-h-[50vh] overflow-y-auto">
          {assets.isLoading && <p className="text-sm text-muted">Loading library…</p>}
          {assets.isError && (
            <p className="text-sm text-muted">
              The media library isn't available right now — you can still paste an image URL.
            </p>
          )}
          {assets.isSuccess && images.length === 0 && (
            <p className="text-sm text-muted">No images in the library yet — upload one above.</p>
          )}
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {images.map((a) => {
              const url = `${R2_PUBLIC_BASE.replace(/\/$/, "")}/${a.r2_key}`;
              return (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => onPick(url)}
                    className="block w-full overflow-hidden rounded-card border border-border bg-card text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <img src={url} alt={a.alt ?? a.filename} className="h-28 w-full object-cover" loading="lazy" />
                    <span className="block truncate px-2 py-1.5 text-xs text-muted">{a.filename}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────────── lists ─────────────────────────────── */

function ListEditor({
  spec,
  idPrefix,
  items,
  onChange,
}: {
  spec: ListSpec;
  idPrefix: string;
  items: Doc[];
  onChange: (items: Doc[]) => void;
}) {
  // A select field defaults to its first option rather than "" — an empty
  // string would fail the option list's own validation on save, silently
  // reverting the whole block back to its default (see resolveBlock).
  const blank = (): Doc =>
    Object.fromEntries(spec.fields.map((f) => [f.key, f.kind === "select" ? (f.options?.[0]?.value ?? "") : ""]));
  const update = (i: number, key: string, value: string) =>
    onChange(items.map((it, j) => (j === i ? { ...it, [key]: value } : it)));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  };
  const sortByDate = () => {
    if (!spec.dateField) return;
    const field = spec.dateField;
    onChange(
      [...items].sort((a, b) => String(a[field] ?? "").localeCompare(String(b[field] ?? ""))),
    );
  };

  return (
    <fieldset className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <legend className="font-heading font-bold text-ink">{spec.label}</legend>
        {spec.dateField && items.length > 1 && (
          <Button type="button" size="sm" variant="outline" onClick={sortByDate}>
            <ArrowUpDown className="mr-1.5 h-4 w-4" aria-hidden />
            Sort by date
          </Button>
        )}
      </div>
      {spec.help && <p className="text-xs text-muted">{spec.help}</p>}
      {items.length === 0 && <p className="text-sm text-muted">Nothing here yet.</p>}
      <ol className="space-y-3">
        {items.map((item, i) => (
          <li key={i} className="rounded-card border border-border bg-paper p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <span className="text-xs font-bold uppercase tracking-wide text-muted">
                {spec.itemLabel} {i + 1}
              </span>
              <span className="flex gap-1">
                <Button type="button" size="sm" variant="ghost" aria-label="Move up" onClick={() => move(i, -1)} disabled={i === 0}>
                  <ArrowUp className="h-4 w-4" aria-hidden />
                </Button>
                <Button type="button" size="sm" variant="ghost" aria-label="Move down" onClick={() => move(i, 1)} disabled={i === items.length - 1}>
                  <ArrowDown className="h-4 w-4" aria-hidden />
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  aria-label={`Remove ${spec.itemLabel} ${i + 1}`}
                  className="text-error"
                  onClick={() => onChange(items.filter((_, j) => j !== i))}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              </span>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {spec.fields.map((f) => (
                <Field
                  key={f.key}
                  spec={f}
                  id={`${idPrefix}-${i}-${f.key}`}
                  value={String(item[f.key] ?? "")}
                  onChange={(v) => update(i, f.key, v)}
                  hint={
                    spec.dateField === f.key
                      ? (weekdayLabel(String(item[f.key] ?? "")) ||
                        (item[f.key] ? "Not a valid date" : undefined))
                      : undefined
                  }
                />
              ))}
            </div>
          </li>
        ))}
      </ol>
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...items, blank()])}>
        <Plus className="mr-1.5 h-4 w-4" aria-hidden />
        Add {spec.itemLabel}
      </Button>
    </fieldset>
  );
}

// Keep the defaults referenced so a block key typo in BLOCK_SPECS fails the
// build rather than silently rendering an empty editor.
void (DEFAULTS satisfies Record<BlockKey, unknown>);
